// =============================================================================
// ResourceSidebar - Pannello laterale filtri risorse per lo scheduler
// =============================================================================

import { useState, useMemo, useEffect } from 'react';
import { Filter, X, Search, ChevronDown, ChevronUp, CheckSquare, Square } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { L } from '../../../config/labels';
import type { Resource } from '../../../types';
import type { ResourceSidebarProps } from '../types/scheduler.types';

export function ResourceSidebar({ 
  resources, 
  resourceTypes, 
  selectedTypes,
  selectedResourceIds,
  searchTerm,
  onToggleType,
  onSetTypes,
  onToggleResource,
  onSetResources,
  onSearchChange,
  onResetFilters,
  isOpen,
  onToggleSidebar,
}: ResourceSidebarProps) {
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());
  const { t } = useTranslation('scheduler');
  
  // Risorse raggruppate per tipo
  const resourcesByType = useMemo(() => {
    const grouped = new Map<string, Resource[]>();
    resources.forEach((resource) => {
      const current = grouped.get(resource.resourceTypeId) || [];
      grouped.set(resource.resourceTypeId, [...current, resource]);
    });
    return grouped;
  }, [resources]);

  // Risorse filtrate per tipo (tramite ricerca testuale)
  const filteredResourcesByType = useMemo(() => {
    const filtered = new Map<string, Resource[]>();
    resourceTypes.forEach((type) => {
      const typeResources = resourcesByType.get(type.id) || [];
      const matching = searchTerm
        ? typeResources.filter((r) =>
            r.name.toLowerCase().includes(searchTerm.toLowerCase())
          )
        : typeResources;
      filtered.set(type.id, matching);
    });
    return filtered;
  }, [resourceTypes, resourcesByType, searchTerm]);

  // Auto-espandi categorie quando si cerca
  useEffect(() => {
    if (searchTerm) {
      const typesWithMatches = new Set<string>();
      filteredResourcesByType.forEach((res, typeId) => {
        if (res.length > 0) typesWithMatches.add(typeId);
      });
      setExpandedTypes(typesWithMatches);
    }
  }, [searchTerm, filteredResourcesByType]);

  const totalVisibleResources = useMemo(() => {
    let count = 0;
    resourceTypes.forEach((type) => {
      const isTypeVisible = selectedTypes === null || selectedTypes.includes(type.id);
      if (isTypeVisible) {
        const typeFiltered = filteredResourcesByType.get(type.id) || [];
        if (selectedResourceIds.length === 0) {
          count += typeFiltered.length;
        } else {
          count += typeFiltered.filter(r => selectedResourceIds.includes(r.id)).length;
        }
      }
    });
    return count;
  }, [resourceTypes, selectedTypes, selectedResourceIds, filteredResourcesByType]);

  const toggleExpanded = (typeId: string) => {
    setExpandedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(typeId)) next.delete(typeId);
      else next.add(typeId);
      return next;
    });
  };

  // Seleziona/deseleziona tutte le risorse di un tipo
  const toggleAllResourcesOfType = (typeId: string) => {
    const typeResources = filteredResourcesByType.get(typeId) || [];
    const typeResourceIds = typeResources.map(r => r.id);
    
    if (selectedResourceIds.length === 0) {
      const otherIds = resources.filter(r => r.resourceTypeId !== typeId).map(r => r.id);
      onSetResources(otherIds);
    } else {
      const allSelected = typeResourceIds.every(id => selectedResourceIds.includes(id));
      if (allSelected) {
        const newIds = selectedResourceIds.filter(id => !typeResourceIds.includes(id));
        onSetResources(newIds.length === resources.length ? [] : newIds);
      } else {
        const newIds = [...new Set([...selectedResourceIds, ...typeResourceIds])];
        onSetResources(newIds.length >= resources.length ? [] : newIds);
      }
    }
  };

  const hasActiveFilters = selectedTypes !== null || selectedResourceIds.length > 0 || searchTerm.length > 0;

  // Stato del selettore globale sui tipi: `null` e "tutti elencati" sono
  // entrambi "tutto selezionato", `[]` e' l'unico stato davvero vuoto.
  const allTypeIds = useMemo(() => resourceTypes.map((type) => type.id), [resourceTypes]);
  const allTypesSelected = selectedTypes === null || selectedTypes.length === allTypeIds.length;
  const noTypeSelected = selectedTypes !== null && selectedTypes.length === 0;
  const someTypesSelected = !allTypesSelected && !noTypeSelected;

  const toggleAllTypes = () => onSetTypes(allTypesSelected ? [] : null);

  // Toggle singola risorsa con gestione transizione da "tutte" a "esplicito"
  const handleToggleResource = (resourceId: string) => {
    if (selectedResourceIds.length === 0) {
      const allIds = resources.map(r => r.id).filter(id => id !== resourceId);
      onSetResources(allIds);
    } else if (selectedResourceIds.length === 1 && selectedResourceIds.includes(resourceId)) {
      onSetResources([]);
    } else {
      onToggleResource(resourceId);
    }
  };

  // A pannello chiuso non resta nessuna linguetta sulla griglia: il bottone
  // "Filtri" della toolbar riapre il pannello e mostra gia' il conteggio dei
  // filtri attivi.
  if (!isOpen) return null;

  return (
    <aside className="w-72 bg-white border-r border-gray-200 flex flex-col flex-shrink-0 sticky top-0 h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
          <Filter className="w-4 h-4 text-emerald-600" />
          {t('sidebar.title')}
        </h2>
        <button
          onClick={onToggleSidebar}
          className="p-1.5 hover:bg-gray-200 rounded-md transition-colors"
          title={t('sidebar.hide')}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      
      {/* Input ricerca */}
      <div className="p-3 border-b border-gray-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={t('sidebar.searchResource', { entity: L.resource.toLowerCase() })}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
          {searchTerm && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
            >
              <X className="w-3 h-3 text-gray-400" />
            </button>
          )}
        </div>
      </div>

      {/* Barra filtri attivi */}
      {hasActiveFilters && (
        <div className="px-3 py-2 bg-emerald-50 border-b border-emerald-100 flex items-center justify-between">
          <span className="text-xs text-emerald-700 font-medium">
            {totalVisibleResources} {t('sidebar.ofResources')} {resources.length} {L.resources.toLowerCase()}
          </span>
          <button
            onClick={onResetFilters}
            className="text-xs text-emerald-600 hover:text-emerald-800 font-medium flex items-center gap-1"
          >
            <X className="w-3 h-3" />
            Reset
          </button>
        </div>
      )}
      
      {/* Intestazione tipi risorsa con selezione globale */}
      {resourceTypes.length > 0 && (
        <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between gap-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide truncate">
            {t('sidebar.resourceTypes')}
          </span>
          <button
            onClick={toggleAllTypes}
            className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-emerald-600 hover:bg-emerald-50 rounded transition-colors flex-shrink-0"
            title={allTypesSelected ? t('sidebar.deselectAllTypes') : t('sidebar.selectAllTypes')}
          >
            {allTypesSelected
              ? <CheckSquare className="w-3.5 h-3.5" />
              : <Square className={cn('w-3.5 h-3.5', someTypesSelected && 'text-emerald-400')} />
            }
            {allTypesSelected ? t('sidebar.deselectAllTypes') : t('sidebar.selectAllTypes')}
          </button>
        </div>
      )}

      {/* Nessun tipo attivo: il calendario resta vuoto, meglio dirlo */}
      {noTypeSelected && (
        <div className="px-3 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-100">
          {t('sidebar.noTypeSelectedHint')}
        </div>
      )}

      {/* Lista tipi risorsa */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-2 space-y-0.5">
          {resourceTypes.map((type) => {
            const typeResources = resourcesByType.get(type.id) || [];
            const filteredRes = filteredResourcesByType.get(type.id) || [];
            const isTypeSelected = selectedTypes === null || selectedTypes.includes(type.id);
            const isExpanded = expandedTypes.has(type.id);
            const totalCount = typeResources.length;
            const visibleCount = filteredRes.length;
            
            const selectedInType = selectedResourceIds.length > 0
              ? filteredRes.filter(r => selectedResourceIds.includes(r.id)).length
              : visibleCount;
            const allInTypeSelected = selectedResourceIds.length === 0 || 
              (filteredRes.length > 0 && filteredRes.every(r => selectedResourceIds.includes(r.id)));
            const someInTypeSelected = selectedResourceIds.length > 0 && 
              filteredRes.some(r => selectedResourceIds.includes(r.id)) && !allInTypeSelected;
            
            if (searchTerm && visibleCount === 0) return null;
            
            return (
              <div key={type.id} className="rounded-lg overflow-hidden">
                {/* Header tipo */}
                <div 
                  className={cn(
                    'flex items-center gap-2 p-2 rounded-lg transition-colors cursor-pointer',
                    isTypeSelected ? 'bg-white hover:bg-gray-50' : 'bg-gray-100/70 opacity-50 hover:opacity-70'
                  )}
                  onClick={() => toggleExpanded(type.id)}
                >
                  <label className="flex items-center cursor-pointer" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isTypeSelected}
                      onChange={() => onToggleType(type.id, allTypeIds)}
                      className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                    />
                  </label>
                  
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: type.color || '#6B7280' }}
                  />
                  <span className={cn(
                    'font-medium text-sm flex-1 truncate',
                    isTypeSelected ? 'text-gray-900' : 'text-gray-500'
                  )}>
                    {type.name}
                  </span>
                  
                  <span className={cn(
                    'text-xs px-1.5 py-0.5 rounded-full tabular-nums',
                    isTypeSelected 
                      ? (selectedResourceIds.length > 0 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-gray-100 text-gray-600')
                      : 'bg-gray-200 text-gray-500'
                  )}>
                    {selectedResourceIds.length > 0 
                      ? `${selectedInType}/${totalCount}` 
                      : (searchTerm ? `${visibleCount}/${totalCount}` : totalCount)
                    }
                  </span>
                  
                  {isExpanded 
                    ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  }
                </div>
                
                {/* Lista risorse espansa */}
                {isExpanded && visibleCount > 0 && (
                  <div className="ml-2 mr-1 mb-1">
                    {visibleCount > 1 && (
                      <button
                        onClick={() => toggleAllResourcesOfType(type.id)}
                        className="flex items-center gap-2 w-full px-2 py-1 text-xs text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={allInTypeSelected}
                          ref={el => { if (el) el.indeterminate = someInTypeSelected; }}
                          onChange={() => toggleAllResourcesOfType(type.id)}
                          className="w-3.5 h-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                        />
                        <span className="font-medium">
                          {allInTypeSelected ? t('sidebar.deselectAll') : t('sidebar.selectAll')}
                        </span>
                      </button>
                    )}
                    {filteredRes.map((resource) => {
                      const isResourceSelected = selectedResourceIds.length === 0 || selectedResourceIds.includes(resource.id);
                      return (
                        <label
                          key={resource.id}
                          className={cn(
                            'flex items-center gap-2 px-2 py-1.5 text-sm rounded cursor-pointer transition-colors',
                            isResourceSelected 
                              ? 'text-gray-700 hover:bg-gray-50' 
                              : 'text-gray-400 hover:bg-gray-50'
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={isResourceSelected}
                            onChange={() => handleToggleResource(resource.id)}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                          />
                          <div
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: resource.color || type.color || '#6B7280' }}
                          />
                          <span className="truncate flex-1">{resource.name}</span>
                          {!resource.isBookable && (
                            <span className="text-xs text-yellow-600 bg-yellow-50 px-1 rounded">{t('sidebar.notBookable')}</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          
          {resourceTypes.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">
              {t('sidebar.noResourceTypes')}
            </div>
          )}
        </div>
      </div>
      
      {/* Footer statistiche */}
      <div className="p-3 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>{resourceTypes.length} {t('sidebar.categories')}</span>
          <span>{resources.length} {t('sidebar.totalResources')}</span>
        </div>
      </div>
    </aside>
  );
}
